// @ts-nocheck
import { useState, useEffect, forwardRef, useRef } from "react";
import * as Mui from "@mui/material";
import { ArrowDropDownRounded, EmailRounded, PersonRounded } from '@mui/icons-material';
import { usePhoneInput, defaultCountries, parseCountry, FlagImage } from "react-international-phone";
import "react-international-phone/style.css";
import "./styles.scss";
import { ReactComponent as CardsRow } from '@assets/custom-icons/card-brands-row.svg';
import Popper from '@components/Popper';
import { buildPhoneDTO, phoneToE164 } from '@utils/phone';
import { getCountryName, getLocale, t } from '@i18n';

function countryDisplayName(iso2, fallback = '') {
    const code = String(iso2 || '').toUpperCase();

    if (!code) {
        return fallback;
    }

    return getCountryName(code, getLocale(), fallback);
}

const formatFullName = (value) => {
    if (!value) return value;

    // Remove special characters but keep spaces
    const cleanValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");

    // Split into words and capitalize each word
    return cleanValue
        .split(" ")
        .map(word => {
            // Keep prepositions and conjunctions (2 or fewer characters) lowercase
            if (word.length <= 2) return word.toLowerCase();
            // Capitalize first letter, lowercase the rest
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
};

const formatDateDisplay = (value) => {
    if (!value) return value;

    // First, remove all non-digits and limit to 8 digits
    const digits = value.replace(/\D/g, "").slice(0, 8);

    if (digits.length <= 2) {
        return digits;
    } else if (digits.length <= 4) {
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
};

const isValidDate = (day, month, year) => {
    // Convert to numbers
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);

    // Basic validation
    if (isNaN(d) || isNaN(m) || isNaN(y)) return false;
    if (d < 1 || d > 31) return false;
    if (m < 1 || m > 12) return false;
    if (y < 1 || y > 9999) return false;

    // Check days in month
    const daysInMonth = new Date(y, m, 0).getDate();
    return d <= daysInMonth;
};

const formatDateExternal = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "");

    if (digits.length < 8) return value;

    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);

    // Validate date before returning
    if (!isValidDate(day, month, year)) return value;

    return `${year}-${month}-${day}`;
};

const convertExternalToDisplay = (externalDate) => {
    if (!externalDate) return "";
    const [year, month, day] = externalDate.split("-");
    if (!year || !month || !day) return "";

    // Validate date before converting
    if (!isValidDate(day, month, year)) return "";

    return `${day}/${month}/${year}`;
};

const formatCPF = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 3) {
        return digits;
    } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    } else if (digits.length <= 9) {
        return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    } else {
        return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    }
};

const formatMoney = (value) => {
    if (!value) return t('input.money.placeholder');

    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Convert to number and divide by 100 to get the correct decimal places
    const amount = parseInt(digits, 10) / 100;

    return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const formatCardNumber = (value) => {
    if (!value) return value;

    // Remove all non-digits and limit to 16 digits
    const digits = value.replace(/\D/g, "").slice(0, 16);

    // Format as 0000 0000 0000 0000
    if (digits.length <= 4) {
        return digits;
    } else if (digits.length <= 8) {
        return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    } else if (digits.length <= 12) {
        return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
    } else {
        return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12)}`;
    }
};

const BaseInput = forwardRef(({ value, onInput, label, placeholder, className, bypassStyles, dataError, ...props }, ref) => {
    const inputClassName = bypassStyles
        ? `custom-input bypass-styles ${className || ''}`.trim()
        : `custom-input ${className || ''}`.trim();

    const variant = bypassStyles ? "outlined" : "filled";

    // Função para corrigir estilos após autocomplete
    const fixAutocompleteStyles = () => {
        setTimeout(() => {
            const inputs = document.querySelectorAll('.custom-input input');
            inputs.forEach(input => {
                if (input.matches(':-webkit-autofill')) {
                    const parent = input.closest('.MuiInputBase-root');
                    if (parent) {
                        if (input.closest('.bypass-styles')) {
                            parent.style.backgroundColor = 'transparent';
                        } else {
                            parent.style.backgroundColor = 'var(--elevation-01)';
                        }
                    }
                }
            });
        }, 100);
    };

    // Detectar quando o autocomplete é aplicado
    useEffect(() => {
        fixAutocompleteStyles();

        // Observar mudanças no DOM para detectar autocomplete
        const observer = new MutationObserver(fixAutocompleteStyles);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });

        // Também escutar eventos de input para detectar autocomplete
        const handleInput = () => fixAutocompleteStyles();
        document.addEventListener('input', handleInput);

        return () => {
            observer.disconnect();
            document.removeEventListener('input', handleInput);
        };
    }, []);

    return (
        <Mui.TextField
            ref={ref}
            fullWidth
            label={label || ''}
            placeholder={placeholder}
            value={value || ''}
            variant={variant}
            onChange={(e) => onInput(e.target.value)}
            className={inputClassName}
            data-error={dataError}
            {...props}
        />
    );
});

const TextInput = ({ value, onInput, label, placeholder, size, className, dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
        setLocalValue(value || "");
    }, [value]);

    const handleChange = (newValue) => {
        if ((size && (localValue?.length < size)) || !size) {
            setLocalValue(newValue);
            onInput(newValue);
        }
    };

    return (
        <div style={{ position: "relative", ...(props.style || {}) }} className={className} data-error={dataError}>
            <BaseInput
                {...props}
                value={localValue}
                onInput={handleChange}
                label={label}
                placeholder={placeholder}
                size={undefined}
                className={undefined}
                dataError={dataError}
            />
            {size && (
                <div className="input-label">
                    {localValue?.length}/{size}
                </div>
            )}
        </div>
    );
};

const FullNameInput = ({ value, onInput, onBlur, label = "", placeholder = t('input.fullName.placeholder'), dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
        setLocalValue(value || "");
    }, [value]);

    const handleChange = (newValue) => {
        const formattedValue = formatFullName(newValue);
        setLocalValue(formattedValue);
        onInput(formattedValue);
    };

    const handleBlur = () => {
        const trimmedValue = localValue.trim();
        setLocalValue(trimmedValue);
        onInput(trimmedValue);
        onBlur?.(trimmedValue);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            onBlur={handleBlur}
            label={label}
            placeholder={placeholder}
            dataError={dataError}
            InputProps={{
                startAdornment: (
                    <Mui.InputAdornment position="start">
                        <PersonRounded className="input-adornment-icon" />
                    </Mui.InputAdornment>
                ),
                ...props.InputProps
            }}
            inputProps={{
                autoComplete: "name",
                ...props.inputProps
            }}
        />
    );
};

// Emits the phone DTO ({ code, number }) instead of a formatted string:
// the country selector picks the dial code and the mask follows the
// selected country's format (react-international-phone).
const PhoneInput = ({ value, onInput, country: countryProp, label = "", placeholder = t('input.phone.placeholder'), dataError, ...props }) => {
    const popperRef = useRef(null);
    const countryBtnRef = useRef(null);

    const { inputValue, handlePhoneValueChange, inputRef, phone, country, setCountry } = usePhoneInput({
        defaultCountry: "br",
        value: phoneToE164(value),
        countries: defaultCountries,
        forceDialCode: true,
        onChange: ({ phone: currentPhone, country: currentCountry }) => {
            onInput?.(buildPhoneDTO(currentCountry.dialCode, currentPhone, currentCountry.iso2));
        },
    });

    // Follows the page-level country selector, but only on an actual prop
    // transition (the ref guard) and only while no national number was
    // typed — a number in progress or a manual flag pick must win.
    const appliedCountryRef = useRef(null);

    useEffect(() => {
        const iso2 = String(countryProp || '').toLowerCase();

        if (!iso2 || appliedCountryRef.current === iso2) {
            return;
        }

        appliedCountryRef.current = iso2;

        if (iso2 === country.iso2 || buildPhoneDTO(country.dialCode, phone)) {
            return;
        }

        if (defaultCountries.some((rawCountry) => parseCountry(rawCountry).iso2 === iso2)) {
            setCountry(iso2);
        }
    }, [countryProp, country, phone, setCountry]);

    function handleOpenCountryPopper() {
        if (!popperRef.current || !countryBtnRef.current) return;

        const options = defaultCountries.map((rawCountry) => {
            const { name, iso2, dialCode } = parseCountry(rawCountry);

            return {
                id: iso2,
                label: `${countryDisplayName(iso2, name)} (+${dialCode})`,
                icon: <FlagImage iso2={iso2} size="18px" style={{ borderRadius: 2, flexShrink: 0 }} />,
                onClick: () => {
                    setCountry(iso2);
                    setTimeout(() => inputRef.current?.focus(), 100);
                },
            };
        });

        popperRef.current.open(options, "bottom-start", countryBtnRef.current, {
            searchable: true,
            searchPlaceholder: t('input.country.searchPlaceholder'),
        });
    }

    return (
        <Popper controlRef={popperRef} className="phone-country-popper">
            <BaseInput
                {...props}
                value={inputValue}
                onInput={() => {}}
                onChange={handlePhoneValueChange}
                inputRef={inputRef}
                label={label}
                placeholder={placeholder}
                dataError={dataError}
                InputProps={{
                    startAdornment: (
                        <Mui.InputAdornment position="start">
                            <span
                                ref={countryBtnRef}
                                className="phone-country-selector"
                                onClick={handleOpenCountryPopper}
                            >
                                <FlagImage
                                    iso2={country.iso2}
                                    size="20px"
                                    style={{ borderRadius: 2, display: "block", flexShrink: 0 }}
                                />
                                <ArrowDropDownRounded sx={{ fontSize: 16 }} />
                            </span>
                        </Mui.InputAdornment>
                    ),
                    ...props.InputProps
                }}
                inputProps={{
                    type: "tel",
                    autoComplete: "tel",
                    ...props.inputProps
                }}
            />
        </Popper>
    );
};

const DateInput = ({ value, onInput, label = t('input.date.label'), placeholder = t('input.date.placeholder'), ...props }) => {
    const [localValue, setLocalValue] = useState(convertExternalToDisplay(value) || "");


    const handleChange = (newValue) => {
        const digits = newValue.replace(/\D/g, "").slice(0, 8);
        const displayValue = formatDateDisplay(digits);
        setLocalValue(displayValue);

        onInput(formatDateExternal(digits));
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
            maxLength={10}
        />
    );
};

const CPFInput = ({ value, onInput, label = "", placeholder = t('input.cpf.placeholder'), dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value ? formatCPF(value) : "");

    const handleChange = (newValue) => {
        const digits = newValue.replace(/\D/g, "").slice(0, 11);
        const displayValue = formatCPF(digits);
        setLocalValue(displayValue);
        onInput(digits);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
            maxLength={14}
            dataError={dataError}
            inputProps={{
                autoComplete: "off", // CPF não tem um autoComplete padrão, mas pode ser útil desabilitar
                ...props.inputProps
            }}
        />
    );
};

const MoneyInput = ({ value, onInput, label = t('input.money.label'), placeholder = t('input.money.placeholder'), ...props }) => {
    const [localValue, setLocalValue] = useState(value ? formatMoney(value) : t('input.money.placeholder'));

    const handleChange = (newValue) => {
        // Remove all non-digits
        const digits = newValue.replace(/\D/g, "");

        // Format the display value
        const displayValue = formatMoney(digits);
        setLocalValue(displayValue);

        // Send only the digits to parent
        onInput(digits);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
        />
    );
};

const IntegerInput = ({ value, onInput, label, placeholder, max = 10, dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value || "");

    const handleChange = (newValue) => {
        // Remove all non-digits and limit to max length
        const digits = newValue.replace(/\D/g, "").slice(0, max);

        setLocalValue(digits);
        onInput(digits);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
            maxLength={max}
            dataError={dataError}
        />
    );
};

const AutoCompleteInput = ({ value, onInput, label, placeholder, options = [], getOptionLabel = (option) => option, loading = false, className, bypassStyles, textFieldProps, ...props }) => {
    const [localValue, setLocalValue] = useState(value || null);

    const handleChange = (_, newValue) => {
        setLocalValue(newValue);
        onInput(newValue);
    };

    const inputClassName = bypassStyles
        ? `custom-input bypass-styles ${className || ''}`.trim()
        : `custom-input ${className || ''}`.trim();

    const variant = bypassStyles ? "outlined" : "filled";

    return (
        <Mui.Autocomplete
            options={options}
            getOptionLabel={getOptionLabel}
            loading={loading}
            value={localValue}
            onChange={handleChange}
            renderInput={(params) => (
                <Mui.TextField
                    {...params}
                    className={inputClassName}
                    variant={variant}
                    label={label}
                    placeholder={placeholder}
                    loading={loading}
                    {...textFieldProps}
                    inputProps={{
                        ...params.inputProps,
                        ...(textFieldProps?.inputProps || {})
                    }}
                    InputProps={{
                        ...params.InputProps,
                        ...(textFieldProps?.InputProps || {}),
                        endAdornment: (
                            <>
                                {params.InputProps.endAdornment}
                                {textFieldProps?.InputProps?.endAdornment}
                            </>
                        ),
                        startAdornment: (
                            <>
                                {textFieldProps?.InputProps?.startAdornment}
                                {params.InputProps.startAdornment}
                            </>
                        )
                    }}
                />
            )}
            {...props}
        />
    );
};

const SelectInput = ({ value, onInput, label, placeholder, options = [], className, bypassStyles, dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value || "");
    const [anchorEl, setAnchorEl] = useState(null);
    const popperRef = useRef();

    const selectedOption = options?.find(option => option.value == localValue);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);

        const popperOptions = options?.map(option => ({
            id: option.value,
            label: option.label,
            onClick: () => {
                setLocalValue(option.value);
                onInput(option.value);
                setAnchorEl(null);
            }
        })) || [];

        popperRef.current?.open(popperOptions, "bottom", event.currentTarget);
    };

    return (
        <Popper controlRef={popperRef} className='select-popper'>
            <button
                onClick={handleClick}
                className={`select-button ${bypassStyles ? 'bypass-styles' : ''}`}
                data-error={dataError}
                style={{
                    width: '100%',
                    minHeight: bypassStyles ? '44px' : '52px',
                    border: bypassStyles ? 'none' : 'var(--border-soft)',
                    backgroundColor: bypassStyles ? 'transparent' : 'var(--elevation-01)',
                    borderRadius: bypassStyles ? '0' : 'var(--gap-1)',
                    padding: '12px 18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: bypassStyles ? 'var(--surface-2)' : 'inherit',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontFamily: 'inherit'
                }}
            >
                <span>{selectedOption?.label || placeholder || label || ''}</span>
                <ArrowDropDownRounded color={bypassStyles ? "inherit" : "action"} sx={{ fontSize: 16 }} />
            </button>
        </Popper>
    );
};

const formatCNPJ = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "").slice(0, 14);

    if (digits.length <= 2) {
        return digits;
    } else if (digits.length <= 5) {
        return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    } else if (digits.length <= 8) {
        return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    } else if (digits.length <= 12) {
        return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    } else {
        return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
    }
};

const CNPJInput = ({ value, onInput, label = t('input.cnpj.label'), placeholder = t('input.cnpj.placeholder'), ...props }) => {
    const [localValue, setLocalValue] = useState(value ? formatCNPJ(value) : "");

    const handleChange = (newValue) => {
        const digits = newValue.replace(/\D/g, "").slice(0, 14);
        const displayValue = formatCNPJ(digits);
        setLocalValue(displayValue);
        onInput(digits);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
            maxLength={18}
        />
    );
};

const EmailInput = ({ value, onInput, label = "", placeholder = t('input.email.placeholder'), dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value || "");

    const handleChange = (newValue) => {
        // Remove espaços e converte para minúsculas
        const normalizedValue = newValue.replace(/\s/g, "").toLowerCase();
        setLocalValue(normalizedValue);
        onInput(normalizedValue);
    };

    return (
        <BaseInput
            {...props}
            value={localValue}
            onInput={handleChange}
            label={label}
            placeholder={placeholder}
            dataError={dataError}
            InputProps={{
                startAdornment: (
                    <Mui.InputAdornment position="start">
                        <EmailRounded className="input-adornment-icon" />
                    </Mui.InputAdornment>
                ),
                ...props.InputProps
            }}
            inputProps={{
                autoComplete: "email",
                ...props.inputProps
            }}
        />
    );
};

const CardNumberInput = ({ value, onInput, label = t('input.cardNumber.label'), placeholder = t('input.cardNumber.placeholder'), bypassStyles, dataError, ...props }) => {
    const [localValue, setLocalValue] = useState(value ? formatCardNumber(value) : "");

    const handleChange = (newValue) => {
        // Remove todos os caracteres não numéricos e limita a 16 dígitos
        const digits = newValue.replace(/\D/g, "").slice(0, 16);

        // Formata o valor para exibição
        const formattedValue = formatCardNumber(digits);
        setLocalValue(formattedValue);

        // Envia apenas os dígitos para o componente pai
        onInput(digits);
    };

    const inputClassName = bypassStyles
        ? `custom-input bypass-styles ${props.className || ''}`.trim()
        : `custom-input ${props.className || ''}`.trim();

    const variant = bypassStyles ? "outlined" : "filled";

    return (
        <Mui.TextField
            fullWidth
            label={label || ''}
            placeholder={placeholder}
            value={localValue || ''}
            variant={variant}
            onChange={(e) => handleChange(e.target.value)}
            className={inputClassName}
            maxLength={19} // 16 dígitos + 3 espaços
            data-error={dataError}
            InputProps={{
                endAdornment: (
                    <Mui.InputAdornment position="end">
                        <CardsRow style={{ height: '12px', width: 'auto' }} />
                    </Mui.InputAdornment>
                ),
                ...props.InputProps
            }}
            inputProps={{
                autoComplete: "cc-number",
                ...props.inputProps
            }}
            {...props}
        />
    );
};

// Create the compound component
const Input: any = Object.assign(BaseInput, {
    Text: TextInput,
    Integer: IntegerInput,
    FullName: FullNameInput,
    Phone: PhoneInput,
    Date: DateInput,
    CPF: CPFInput,
    Money: MoneyInput,
    AutoComplete: AutoCompleteInput,
    Select: SelectInput,
    CNPJ: CNPJInput,
    Email: EmailInput,
    CardNumber: CardNumberInput
});

export default Input;
